import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { lazy, Suspense, useEffect, useId, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { brandPath, type NavMenu } from '@/config/navigation';

import { navItemSx } from './navItemSx';

// Fetched when a menu is first opened, not on every page load.
const NavDropdown = lazy(() => import('./NavDropdown'));

type Props = { menu: NavMenu; color: string; solid: boolean };

/** Gives the pointer time to cross the gap into the panel before it closes. */
const CLOSE_DELAY_MS = 200;

/**
 * Fallback distance from the pill to the panel, used only if the header
 * cannot be found above the button — which should not happen inside
 * `SiteHeader`, but a panel 8px under its trigger is a sane shape to fall
 * back to and is what this menu shipped with before it learned to align.
 */
const FALLBACK_PANEL_OFFSET = 8;

/**
 * Hover should only drive the menu on devices that actually have a hover
 * state. Without this guard, a touchscreen's synthetic "enter" on first tap
 * would pop the panel open and swallow the tap that was meant to follow the
 * link. Read once: a device's hover capability doesn't change mid-session.
 */
const HOVER_CAPABLE =
  typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

export function NavMenuButton({ menu, color, solid }: Props) {
  const [open, setOpen] = useState(false);
  /** Only a real activation (click, Enter/Space) should steal focus into the
   *  list; a hover-open must leave focus alone. */
  const [autoFocusItem, setAutoFocusItem] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const id = useId();
  const { pathname } = useLocation();
  /**
   * Every page this menu can reach, so the pill can mark itself as the one
   * holding the current route.
   *
   * `groups` is in here and used not to be, which was a quiet bug worth
   * naming: a grouped mega-menu's links live only in `groups`, never in
   * `items`, so the Services pill never lit up on any of the service
   * pages it leads to — the menu offered no clue that the page you were on
   * was inside it. The header's "More" pill made that impossible to live
   * with, because every menu it folds away arrives as a group, so up to four
   * nav items at once would have lost their active state the moment the row
   * got tight.
   */
  const paths = [
    ...menu.items.map((i) => i.to),
    ...(menu.groups ?? []).flatMap((group) => group.items.map((i) => i.to)),
    ...(menu.brands ?? []).map(brandPath),
    // A menu entry may be a filtered page ('/cars?body=commercial'), and a
    // query string is not part of a route pattern: left on, `matchPath` reads
    // the '?' as the pattern syntax that makes the preceding segment optional
    // and matches whatever it likes. The pill marks the page, so the path is
    // all it ever needed.
  ].map((to) => to.split('?')[0] ?? to);
  const active = paths.some((to) => matchPath({ path: to, end: true }, pathname));

  /**
   * How far below the pill the panel's top edge has to sit for it to line up
   * with the header's bottom border.
   *
   * The pill is centred in a 72px toolbar, so its own bottom edge is some
   * 16px short of the bar — anchoring the panel to the button alone hangs it
   * over the header, which is what this measures away. The number is the live
   * gap between the two bottoms, so it answers for the toolbar's height, the
   * pill's own height and the breakpoint they change at without any of the
   * three being written down twice.
   *
   * It is a function rather than a measured value in state because Popper
   * calls it on every position update: the header has rows that collapse on
   * scroll and a ribbon that can be dismissed, and a number captured when the
   * menu opened would describe a header that has since moved.
   */
  const panelOffset = () => {
    const button = anchorRef.current;
    const header = button?.closest('header');
    if (!button || !header) return FALLBACK_PANEL_OFFSET;
    return header.getBoundingClientRect().bottom - button.getBoundingClientRect().bottom;
  };

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const openNow = (focusItem: boolean) => {
    cancelClose();
    setEverOpened(true);
    setAutoFocusItem(focusItem);
    setOpen(true);
  };

  const closeSoon = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  const closeNow = () => {
    cancelClose();
    setOpen(false);
  };

  useEffect(() => cancelClose, []);

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative' }}
      onMouseEnter={HOVER_CAPABLE ? () => openNow(false) : undefined}
      onMouseLeave={HOVER_CAPABLE ? closeSoon : undefined}
      onBlur={(e) => {
        // Closes once focus leaves both the button and the panel; a click or
        // Tab that lands back inside the container is not a real blur.
        if (!containerRef.current?.contains(e.relatedTarget as Node | null)) closeNow();
      }}
    >
      <Button
        ref={anchorRef}
        id={`${id}-button`}
        aria-controls={open ? `${id}-menu` : undefined}
        aria-haspopup="true"
        aria-expanded={open}
        data-active={active}
        onClick={() => (open ? closeNow() : openNow(true))}
        endIcon={
          <ExpandMore
            sx={{
              transition: 'transform 150ms',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        }
        sx={[navItemSx(color, solid), { '& .MuiButton-endIcon': { ml: 1 } }]}
      >
        {menu.label}
      </Button>
      {everOpened && (
        <Suspense fallback={null}>
          <NavDropdown
            menu={menu}
            id={id}
            open={open}
            anchor={anchorRef.current}
            offset={panelOffset}
            autoFocusItem={autoFocusItem}
            onClose={closeNow}
            onEscape={() => {
              closeNow();
              anchorRef.current?.focus();
            }}
          />
        </Suspense>
      )}
    </Box>
  );
}
